/**
 * O que o aparelho lembra sobre o push (plano "Push: religar sozinho ou avisar", 29/10/2026).
 *
 * Dois grupos, que o "Sair" trata de jeitos opostos:
 *
 * - **Do aparelho** (sobrevive ao "Sair"): a intenção (a pessoa ligou ou desligou — Decisão 3), a marca da religação única (Decisão 4)
 *   e os silêncios do aviso (Decisão 8). Desligar é uma decisão deste aparelho, e a pessoa seguinte nele a herda.
 * - **Da sessão do app** (o `soltarDonoDoPush` apaga em toda saída de sessão — Decisões 2 e 6): a confirmação do servidor, o carimbo da
 *   última sincronização e as falhas seguidas do religar. Uma por app (`interno`/`portal`): a do interno não vale para o portal.
 *
 * Tudo em `localStorage`, menos o "aviso já mostrado", que mora no `sessionStorage` (uma vez por sessão de aba). Melhor-esforço, como
 * o `donoDoPush.ts`: o armazenamento pode lançar (aba privada, bloqueado) e então cada função devolve o valor neutro, sem derrubar nada.
 *
 * Módulo puro de propósito — sem contexto, sem axios, sem `@/services/api`: o `donoDoPush.ts` o importa, e o Portal importa o
 * `donoDoPush.ts` (não pode tocar a API interna). Do `donoDoPush.ts` só entra o TIPO `AppDoPush`: sem ciclo de valor.
 */
import type { AppDoPush } from "./donoDoPush";

/** `ligado`: a pessoa quer o push neste aparelho; `desligado`: apertou Desligar. */
export type IntencaoDoPush = "ligado" | "desligado";

/** `religar`: permissão voltou a `default` ou o religar sozinho não deu; `desbloquear`: permissão `denied`. */
export type TipoDeSilencio = "religar" | "desbloquear";

/**
 * Os avisos que a tela mostra (o `qual` do resultado `aviso` do `sincronizarPush`, que deriva o tipo `QualAviso` desta lista). O
 * "já mostrado" de cada um mora no `sessionStorage`, e o `limparSilencios` precisa conhecê-los todos para zerá-lo.
 */
export const QUAIS_AVISOS_DO_PUSH = [
  "permissao-voltou-a-perguntar",
  "bloqueado",
  "precisa-de-clique",
] as const;

/** Disparado no `window` quando algo mudou no push deste aparelho (a tela relê o estado; nunca dispara outra sincronização). */
export const EVENTO_PUSH_MUDOU = "seweb:push-mudou";

/** O aviso `precisa-de-clique` só aparece na 3ª falha seguida do religar sozinho. */
export const FALHAS_ATE_AVISAR = 3;

/** Carimbo da última sincronização: quando (epoch ms) e com que permissão do navegador. */
export interface CarimboDaSincronizacao {
  em: number;
  permissao: string;
}

const CHAVE_DA_INTENCAO = "seweb:push-intencao";
const chaveDoReligarUnico = (app: AppDoPush) => `seweb:push-religar-unico:${app}`;
const chaveDaConfirmacao = (app: AppDoPush) => `seweb:push-confirmado:${app}`;
const chaveDoCarimbo = (app: AppDoPush) => `seweb:push-sync-em:${app}`;
const chaveDasFalhas = (app: AppDoPush) => `seweb:push-religar-falhas:${app}`;
const chaveDoSilencio = (tipo: TipoDeSilencio) => `seweb:push-aviso-ate:${tipo}`;
const chaveDoAvisoVisto = (qual: string) => `seweb:push-aviso-visto:${qual}`;

const TIPOS_DE_SILENCIO: readonly TipoDeSilencio[] = ["religar", "desbloquear"];
const UM_DIA_EM_MS = 24 * 60 * 60 * 1000;

type Armazenamento = "localStorage" | "sessionStorage";

// O acesso a `window.localStorage` em si pode lançar (SecurityError), por isso fica dentro do `try`; e é feito a cada chamada, nunca
// guardado em constante de módulo.
function lerChave(onde: Armazenamento, chave: string): string | null {
  try {
    return window[onde].getItem(chave);
  } catch {
    return null;
  }
}

function gravarChave(onde: Armazenamento, chave: string, valor: string): void {
  try {
    window[onde].setItem(chave, valor);
  } catch {
    // sem armazenamento: o push segue funcionando, só sem a memória (cai no comportamento de quem nunca gravou nada)
  }
}

function apagarChave(onde: Armazenamento, chave: string): void {
  try {
    window[onde].removeItem(chave);
  } catch {
    // idem `gravarChave`
  }
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Do aparelho: a intenção, a marca da religação única e os silêncios
// ---------------------------------------------------------------------------------------------------------------------------------

/** Se a pessoa ligou ou desligou o push neste aparelho; `null` sem valor, com valor desconhecido ou sem armazenamento. */
export function lerIntencaoDoPush(): IntencaoDoPush | null {
  const valor = lerChave("localStorage", CHAVE_DA_INTENCAO);
  return valor === "ligado" || valor === "desligado" ? valor : null;
}

export function registrarIntencaoDoPush(intencao: IntencaoDoPush): void {
  gravarChave("localStorage", CHAVE_DA_INTENCAO, intencao);
}

/**
 * Apaga a intenção (volta a "sem intenção"). É o que o clique em Ligar faz com o `desligado` ANTES de inscrever (Decisão 3: "`desligado`
 * só sai num clique de Ligar"): entre o `subscribe()` e o POST do clique já há inscrição no navegador e o `ligado` ainda não foi gravado,
 * e uma sincronização que visse `desligado` nessa janela (esta aba ou outra) desfaria a inscrição que a pessoa acabou de pedir.
 */
export function limparIntencaoDoPush(): void {
  apagarChave("localStorage", CHAVE_DA_INTENCAO);
}

/**
 * O aparelho já tentou a religação única deste `app`? Qualquer valor na chave conta como "já tentou": a marca existe para impedir laço,
 * então na dúvida (valor estranho) não se tenta de novo.
 */
export function jaTentouOReligarUnico(app: AppDoPush): boolean {
  return lerChave("localStorage", chaveDoReligarUnico(app)) !== null;
}

/** Grava a marca ANTES de tentar (Decisão 4): se a tentativa falhar, não há segunda tentativa sozinha. */
export function registrarReligarUnico(app: AppDoPush): void {
  gravarChave("localStorage", chaveDoReligarUnico(app), "1");
}

/** O aviso `tipo` está calado em `agora` (epoch ms)? Valor que não é número vale como sem silêncio. */
export function avisoSilenciado(tipo: TipoDeSilencio, agora: number): boolean {
  const ate = Number(lerChave("localStorage", chaveDoSilencio(tipo)));
  return Number.isFinite(ate) && agora < ate;
}

/** Cala o aviso `tipo` por `dias` a partir de `agora` (epoch ms): no instante `agora + dias` ele já pode voltar. */
export function silenciarAviso(tipo: TipoDeSilencio, agora: number, dias: number): void {
  gravarChave("localStorage", chaveDoSilencio(tipo), String(agora + dias * UM_DIA_EM_MS));
}

/**
 * Zera TODOS os silêncios: a causa do aviso sumiu, e o próximo problema deve avisar na hora (Decisão 8). São os dois do `localStorage`
 * (`religar` e `desbloquear`, que calam por dias) E o "já mostrado" dos três avisos no `sessionStorage` (que cala o aviso até a aba
 * fechar). Sem o segundo, o aviso que a pessoa já viu nesta sessão não voltaria, mesmo com a causa tendo sumido e voltado.
 */
export function limparSilencios(): void {
  for (const tipo of TIPOS_DE_SILENCIO) apagarChave("localStorage", chaveDoSilencio(tipo));
  for (const qual of QUAIS_AVISOS_DO_PUSH) apagarChave("sessionStorage", chaveDoAvisoVisto(qual));
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Da sessão do app: a confirmação, o carimbo e as falhas (o `soltarDonoDoPush` os apaga)
// ---------------------------------------------------------------------------------------------------------------------------------

/**
 * Marca curta do endpoint, para só comparar igualdade ("é o mesmo endpoint que o servidor confirmou?"): cyrb53, hash síncrono NÃO
 * criptográfico de 53 bits, em base36 (até 11 caracteres). Nunca vai ao servidor nem a log, e não guarda o endpoint no aparelho.
 * (A marca do servidor, `md5(endpoint)[:10]`, é outra conta, só para o log do worker.)
 */
export function marcaDoEndpoint(endpoint: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < endpoint.length; i++) {
    const c = endpoint.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/** A marca do endpoint que o servidor confirmou a este `app` nesta sessão, ou `null`. */
export function lerConfirmacaoDoPush(app: AppDoPush): string | null {
  return lerChave("localStorage", chaveDaConfirmacao(app));
}

export function registrarConfirmacaoDoPush(app: AppDoPush, marca: string): void {
  gravarChave("localStorage", chaveDaConfirmacao(app), marca);
}

export function limparConfirmacaoDoPush(app: AppDoPush): void {
  apagarChave("localStorage", chaveDaConfirmacao(app));
}

/** O carimbo da última sincronização do `app`; `null` se nunca sincronizou ou se o valor guardado é lixo. */
export function lerUltimaSincronizacao(app: AppDoPush): CarimboDaSincronizacao | null {
  const bruto = lerChave("localStorage", chaveDoCarimbo(app));
  if (bruto === null) return null;
  try {
    const carimbo: unknown = JSON.parse(bruto);
    if (typeof carimbo !== "object" || carimbo === null) return null;
    const { em, permissao } = carimbo as Record<string, unknown>;
    if (typeof em !== "number" || !Number.isFinite(em) || typeof permissao !== "string")
      return null;
    return { em, permissao };
  } catch {
    return null;
  }
}

export function registrarSincronizacao(app: AppDoPush, agora: number, permissao: string): void {
  const carimbo: CarimboDaSincronizacao = { em: agora, permissao };
  gravarChave("localStorage", chaveDoCarimbo(app), JSON.stringify(carimbo));
}

export function limparSincronizacao(app: AppDoPush): void {
  apagarChave("localStorage", chaveDoCarimbo(app));
}

/** Quantas vezes seguidas o religar sozinho falhou neste `app`; 0 sem valor, com lixo ou sem armazenamento. */
export function lerFalhasDoReligar(app: AppDoPush): number {
  const falhas = Number(lerChave("localStorage", chaveDasFalhas(app)));
  return Number.isInteger(falhas) && falhas > 0 ? falhas : 0;
}

/** Soma 1 às falhas seguidas do `app` e devolve o total (sem armazenamento, devolve 1: a soma sobre o 0 neutro). */
export function registrarFalhaDoReligar(app: AppDoPush): number {
  const total = lerFalhasDoReligar(app) + 1;
  gravarChave("localStorage", chaveDasFalhas(app), String(total));
  return total;
}

export function zerarFalhasDoReligar(app: AppDoPush): void {
  apagarChave("localStorage", chaveDasFalhas(app));
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Da aba: o aviso já mostrado, e o evento "o push mudou"
// ---------------------------------------------------------------------------------------------------------------------------------

/** O aviso `qual` já apareceu nesta sessão da aba? (`sessionStorage`: some ao fechar a aba.) */
export function avisoJaMostrado(qual: string): boolean {
  return lerChave("sessionStorage", chaveDoAvisoVisto(qual)) !== null;
}

export function registrarAvisoMostrado(qual: string): void {
  gravarChave("sessionStorage", chaveDoAvisoVisto(qual), "1");
}

/** Desfaz o "já mostrado" do aviso `qual`, e só dele (o aviso que saiu da tela sem a pessoa ter visto o fim: ver o `AvisoDoPush`). */
export function desfazerAvisoMostrado(qual: string): void {
  apagarChave("sessionStorage", chaveDoAvisoVisto(qual));
}

/** Avisa a tela (no mesmo `window`) de que o push deste aparelho mudou, para ela reler o estado. */
export function avisarQueOPushMudou(): void {
  window.dispatchEvent(new Event(EVENTO_PUSH_MUDOU));
}
